/**
 * Shared TypeScript compiler configuration for sandbox code.
 *
 * Two configs live here:
 *   1. `SANDBOX_COMPILER_OPTIONS` — for **runtime transpilation** (ts.transpileModule)
 *   2. `SANDBOX_TYPECHECK_OPTIONS`   — for **type-checking** (Monaco TS worker + backend TypeCheckTool)
 *
 * Both the browser and the AI backend must use these exact configs so
 * there is zero divergence in what errors a user sees versus what the
 * backend reports.
 *
 * Values are raw numbers so consumers don't need to import the ts/monaco
 * enum at the call site. Comments show the corresponding enum members.
 */
import type ts from 'typescript';

export interface SandboxCompilerOptions {
  target: ts.ScriptTarget;
  module: ts.ModuleKind;
  strict: boolean;
  esModuleInterop: boolean;
}

/**
 * The canonical compiler options for sandbox runtime transpilation.
 *
 * WARNING: Changing these changes what code the sandbox can run. Both
 * the browser and the backend depend on these being identical.
 */
export const SANDBOX_COMPILER_OPTIONS = {
  // Values as raw numbers so consumers don't need to import the ts enum
  // at the call site — they match ts.ScriptTarget.ES2020 / ts.ModuleKind.CommonJS.
  target: 7 as unknown as ts.ScriptTarget,     // ts.ScriptTarget.ES2020
  module: 1 as unknown as ts.ModuleKind,        // ts.ModuleKind.CommonJS
  strict: false,
  esModuleInterop: true,
} satisfies SandboxCompilerOptions;

/**
 * The canonical compiler options for sandbox **type-checking**.
 *
 * These are used by BOTH Monaco (frontend) and TypeCheckTool (backend)
 * to guarantee identical error reporting. The frontend is authoritative —
 * the user only sees Monaco's squiggles, so the backend must match.
 *
 * NOTE: These differ from the runtime transpilation options above.
 * Type-checking targets ES2020 with ESNext modules (matching the
 * browser's ES module analysis), while runtime transpiles to CommonJS
 * for the sandbox's `new Function()` evaluator.
 */
export const SANDBOX_TYPECHECK_OPTIONS: ts.CompilerOptions = {
  target: 7 as unknown as ts.ScriptTarget,            // ts.ScriptTarget.ES2020
  module: 99 as unknown as ts.ModuleKind,              // ts.ModuleKind.ESNext
  moduleResolution: 2 as unknown as ts.ModuleResolutionKind, // ts.ModuleResolutionKind.NodeJs
  allowNonTsExtensions: true,
  strict: false,
  noEmit: true,
  esModuleInterop: true,
  allowJs: true,
  checkJs: false,
  noImplicitAny: false,
  noImplicitThis: false,
  strictNullChecks: false,
  lib: ['lib.es2020.d.ts', 'lib.dom.d.ts'],
  skipLibCheck: true,
  isolatedModules: true,
  jsx: 4 as unknown as ts.JsxEmit,                    // ts.JsxEmit.ReactJSX
  noResolve: false,
  types: [],
  // Workspace alias `@/foo/bar` → resolves against project root. Must
  // match sandbox-core/src/moduleResolver.ts, which strips the `@/`
  // prefix at runtime. Without this, Monaco shows TS2307 squiggles for
  // every project-internal import using the alias even though the
  // sandbox runtime loads them just fine.
  baseUrl: '/',
  paths: {
    '@/*': ['*'],
  },
};

/**
 * Monaco-specific type-check options.
 *
 * Monaco registers each file of the project tree as a model whose URI carries
 * the `file:///` scheme and the tree path (e.g. `file:///src/shared/constants.ts`). TypeScript resolves
 * `baseUrl`-relative and `paths` (`@/*`) candidates as plain path strings, so
 * with `baseUrl: '/'` the `@/shared/constants` alias resolves to the
 * scheme-LESS `/shared/constants.ts`, which never matches a `file:///…` model
 * → **TS2307 on every `@/` import in the editor** (relative imports escape this
 * because they inherit the importing model's `file://` scheme).
 *
 * Setting `baseUrl` to `file:///src/` makes the alias/baseUrl candidates carry the
 * same scheme as the models and the tree's `src/` root — a project's tsconfig maps
 * `@/*` to `./src/*` — so `@/shared/constants` → `file:///src/shared/constants.ts`
 * resolves. `@gotti/*` (extraLibs under `file:///node_modules/@gotti/…`) and
 * relative imports are unaffected.
 *
 * The backend `TypeCheckTool` keeps `SANDBOX_TYPECHECK_OPTIONS` (`baseUrl: '/'`)
 * because its virtual FS is scheme-less, and resolves `@/` via an explicit
 * resolver — same behavior, different filesystem. The `aliasResolution.spec.ts`
 * guard asserts BOTH resolve `@/`.
 */
export const SANDBOX_MONACO_TYPECHECK_OPTIONS: ts.CompilerOptions = {
  ...SANDBOX_TYPECHECK_OPTIONS,
  baseUrl: 'file:///src/',
};

/**
 * TypeScript diagnostic codes that should be suppressed when
 * validating sandbox code — shared between the Monaco editor on the
 * frontend and the TypeCheckTool / RunTestsTool on the backend.
 *
 * Each entry has a comment explaining why it's ignored.
 *
 * IMPORTANT: This list is consumed by the backend via `shouldSuppressDiagnostic()`
 * which ALSO checks the diagnostic message. Do NOT pass this list directly to
 * Monaco's `diagnosticCodesToIgnore` — that would suppress ALL instances of
 * these codes, including real user errors. Use `MONACO_SAFE_CODES_TO_IGNORE`
 * for Monaco's global ignore list.
 */
export const SANDBOX_DIAGNOSTIC_CODES_TO_IGNORE: readonly number[] = [
  // 2307: Cannot find module — @gotti/* packages aren't on the virtual FS.
  2307,
  // 2306: Not a module — cascading from 2307.
  2306,
  // 2792: Cannot find module (ESM-specific variant of 2307).
  2792,
  // 2304: Cannot find name — cascading from unresolved @gotti imports.
  2304,
  // 2305: Module has no exported member — cascading from virtual module stubs.
  2305,
  // 2554: Expected N arguments, got M — overload resolution issues with
  // stripped @gotti type declarations in Monaco.
  2554,
  // 2367: Comparison operand types — enum↔string switch mismatches.
  2367,
  // 2678: Enum type not comparable — same family as 2367.
  2678,
] as const;

/**
 * Subset of diagnostic codes that are safe to suppress GLOBALLY in Monaco
 * via `diagnosticCodesToIgnore` — i.e. every single instance of these codes
 * is noise, regardless of the error message.
 *
 * Codes like 2307 ("Cannot find module") are deliberately EXCLUDED here
 * because they must only be suppressed when the message mentions @gotti/*.
 * If we suppressed 2307 globally, the user would never see errors for bad
 * relative import paths like `../shared/constants` → `./shared/constants`.
 *
 * For the full suppression list (with message-level filtering), use
 * `shouldSuppressDiagnostic()` from diagnostics.ts.
 */
export const MONACO_SAFE_CODES_TO_IGNORE: readonly number[] = [
] as const;
